import React, { useState, useEffect } from 'react';
import api from './api';
import { useAuth } from './AuthContext';
import './Branches.css';

const Branches = () => {
  const { hasRole } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    sort_order: 0,
    description: '',
    manager_name: '',
    manager_phone: '',
    is_active: true,
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const perPage = 20;

  useEffect(() => {
    fetchBranches();
  }, [page, search]);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const params = { per_page: perPage, page };
      if (search) params.search = search;

      const response = await api.get('/branches', { params });
      setBranches(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBranches();
  };

  const calculateNextBranchCode = () => {
    const existingCodes = new Set(branches.map((b) => b.code).filter(Boolean));
    let maxNum = 0;
    for (const code of existingCodes) {
      const m = code.match(/^BR(\d+)$/i);
      if (m) {
        const n = parseInt(m[1], 10);
        if (!isNaN(n) && n > maxNum) maxNum = n;
      }
    }
    let nextNum = maxNum + 1;
    while (nextNum <= 99) {
      const cand = `BR${String(nextNum).padStart(2, '0')}`;
      if (!existingCodes.has(cand)) return cand;
      nextNum++;
    }
    return '';
  };

  const openModal = (branch = null) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({
        name: branch.name,
        code: branch.code || '',
        sort_order: branch.sort_order || 0,
        description: branch.description || '',
        manager_name: branch.manager_name || '',
        manager_phone: branch.manager_phone || '',
        is_active: branch.is_active,
      });
    } else {
      setEditingBranch(null);
      const suggestedCode = calculateNextBranchCode();
      setFormData({
        name: '',
        code: suggestedCode,
        sort_order: branches.length + 1,
        description: '',
        manager_name: '',
        manager_phone: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBranch(null);
    setFormData({
      name: '',
      code: '',
      sort_order: 0,
      description: '',
      manager_name: '',
      manager_phone: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        code: formData.code.trim() || null,
        sort_order: parseInt(formData.sort_order, 10) || 0,
        description: formData.description.trim() || null,
        manager_name: formData.manager_name.trim() || null,
        manager_phone: formData.manager_phone.trim() || null,
        is_active: formData.is_active,
      };

      if (editingBranch) {
        await api.patch(`/branches/${editingBranch.id}`, payload);
      } else {
        await api.post('/branches', payload);
      }
      closeModal();
      fetchBranches();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/branches/${id}`);
      setDeleteConfirm(null);
      fetchBranches();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="branches-page">
      <div className="page-header">
        <h2>Филиалы</h2>
        {hasRole(['admin']) && (
          <button className="btn-primary" onClick={() => openModal()}>
            + Добавить филиал
          </button>
        )}
      </div>

      <div className="filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по названию филиала..."
          />
          <button type="submit" className="btn-secondary">
            Поиск
          </button>
        </form>
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Код</th>
                  <th>Название</th>
                  <th>Руководитель</th>
                  <th>Телефон</th>
                  <th>Порядок</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {branches.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Нет филиалов
                    </td>
                  </tr>
                ) : (
                  branches.map((branch) => (
                    <tr key={branch.id}>
                      <td>{branch.id}</td>
                      <td>
                        {branch.code ? (
                          <span className="branch-code-badge">{branch.code}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td><strong>{branch.name}</strong></td>
                      <td>{branch.manager_name || '-'}</td>
                      <td>{branch.manager_phone || '-'}</td>
                      <td>{branch.sort_order}</td>
                      <td>
                        <span className={`status ${branch.is_active ? 'active' : 'inactive'}`}>
                          {branch.is_active ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                      <td className="actions">
                        {hasRole(['admin']) && (
                          <>
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => openModal(branch)}
                              title="Редактировать"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => setDeleteConfirm(branch)}
                              title="Удалить"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Назад
              </button>
              <span className="page-info">
                Страница {page} из {totalPages} ({total} филиалов)
              </span>
              <button
                className="btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingBranch ? 'Редактировать филиал' : 'Новый филиал'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Название филиала *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Например, Центральный филиал"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Код филиала</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Автоматически (например, BR05)"
                    maxLength={50}
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                    Код филиала (генерируется автоматически, например BR01..BR10)
                  </small>
                </div>
                <div className="form-group">
                  <label>Порядок отображения</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>ФИО руководителя</label>
                <input
                  type="text"
                  value={formData.manager_name}
                  onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                  placeholder="Иванов Иван Иванович"
                />
              </div>

              <div className="form-group">
                <label>Телефон руководителя</label>
                <input
                  type="text"
                  value={formData.manager_phone}
                  onChange={(e) => setFormData({ ...formData, manager_phone: e.target.value })}
                  placeholder="+7 999 123-45-67"
                />
              </div>

              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Дополнительная информация о филиале..."
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {' '}Активен
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingBranch ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <h3>Подтверждение удаления</h3>
            <p>
              Вы уверены, что хотите удалить филиал <strong>{deleteConfirm.name}</strong>?
            </p>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Объекты и пользователи останутся в системе, но их привязка к этому филиалу будет сброшена.
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Отмена
              </button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Branches;
